import dayjs from "dayjs";

/**
 * What the Online Policy list was showing (the filters, the search, the sort, the page, whether the filter panel was
 * open), kept for this browser tab, so opening a policy's History or Edit and coming back shows the same list (the
 * owner, 2026-11-02). History and Edit are pages of their own, so the list page is built again on the way back.
 *
 * sessionStorage: for this tab only, gone when it closes. One entry per login, so the next person to log in on the
 * same tab starts from the defaults (logout does not clear sessionStorage).
 */
const keyFor = () => {
  let user = "";
  try {
    user = sessionStorage.getItem("UserId") || "";
  } catch (e) {
    /* storage blocked */
  }
  return `onlinePolicy.listView.${user}`;
};

export const readListView = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(keyFor()) || "null");
    return saved && typeof saved === "object" && !Array.isArray(saved) ? saved : {};
  } catch (e) {
    return {};
  }
};

/** Merges, so the list's values and the panel's open state can be saved from different places. */
export const saveListView = (changes) => {
  try {
    sessionStorage.setItem(keyFor(), JSON.stringify({ ...readListView(), ...changes }));
  } catch (e) {
    /* private window or blocked storage: the list still works, it just starts fresh next time */
  }
};

const DAY = "YYYY-MM-DD";

/**
 * A date range as it is kept: "thisMonth" when it is the default (so a tab left open into the next month gets the new
 * month, not the old one), [start, end] dates, or [null, null] when the dates were cleared.
 */
export const rangeToSaved = (range, thisMonth) => {
  if (!range || !range[0] || !range[1]) return [null, null];
  const [start, end] = range.map((d) => dayjs(d).format(DAY));
  if (thisMonth && start === dayjs(thisMonth[0]).format(DAY) && end === dayjs(thisMonth[1]).format(DAY)) return "thisMonth";
  return [start, end];
};

/** The other way. Anything unreadable is the default month. */
export const rangeFromSaved = (saved, thisMonth) => {
  if (Array.isArray(saved) && saved.length === 2) {
    if (saved[0] === null && saved[1] === null) return [null, null];
    const [start, end] = saved.map((d) => dayjs(d, DAY));
    if (start.isValid() && end.isValid() && !end.isBefore(start)) return [start.startOf("day"), end.endOf("day")];
  }
  return thisMonth;
};

const SORT_ORDERS = ["asc", "desc"];
export const sortFromSaved = (saved, fallback) =>
  saved && typeof saved.sortBy === "string" && saved.sortBy && SORT_ORDERS.includes(saved.sortOrder) ? saved : fallback;

export const pageFromSaved = (saved) => (Number.isInteger(saved) && saved >= 0 ? saved : 0);

export const rowsPerPageFromSaved = (saved) => (Number.isInteger(saved) && saved > 0 && saved <= 100 ? saved : 10);
